import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Spinner, Alert, Badge, Button, Table,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "auth/useAuth";
import { setupApiAuth } from "../../helpers/auth-helper";
import ApiService from "../../shared/services/apiService";
import { useUserGroups } from "../../shared/hooks/useUserGroups";
import { useCandidatos } from "./CandidatosContext";
import { toast } from "react-toastify";
import { useMediaQuery, MQ } from "../../shared/hooks/useMediaQuery";

const EDUCATION_LABELS = {
  elementary: "Ensino Fundamental",
  highschool: "Ensino Médio",
  college: "Ensino Superior",
  graduate: "Pós-Graduação",
};

const GENDER_LABELS = {
  masculino: "Masculino",
  feminino: "Feminino",
  outros: "Outro",
  prefiro_nao_informar: "Prefiro não informar",
};

const CIVIL_LABELS = {
  solteiro: "Solteiro(a)",
  casado: "Casado(a)",
  divorciado: "Divorciado(a)",
  viuvo: "Viúvo(a)",
  uniao_estavel: "União Estável",
};

const LEVEL_LABELS = {
  basic: "Básico",
  intermediate: "Intermediário",
  advanced: "Avançado",
  specialist: "Especialista",
  proficient: "Proficiente",
};

const formatDateBR = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

const calcAge = (dateStr) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() + 1 - m;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age--;
  return `${age} anos`;
};

// WhatsApp deep link; tolerate numbers stored with or without the +55 prefix.
const whatsappHref = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return null;
  const national = digits.length > 11 && digits.startsWith("55") ? digits.slice(2) : digits;
  return `https://wa.me/55${national}`;
};

// Social handles: full URLs stay as-is; "@usuario" or "usuario" become profile links.
const socialHref = (value, base) => {
  if (!value) return null;
  const v = value.trim();
  if (/^https?:\/\//i.test(v)) return v;
  return `${base}${v.replace(/^@/, "")}`;
};

const SocialLink = ({ value, base }) => {
  const href = socialHref(value, base);
  if (!href) return "—";
  return <a href={href} target="_blank" rel="noopener noreferrer" className="text-break">{value}</a>;
};

const Section = ({ title, children }) => (
  <Card className="mb-3">
    <CardBody>
      <h5 className="mb-3">{title}</h5>
      {children}
    </CardBody>
  </Card>
);

const Field = ({ label, value, md = 4 }) => (
  <Col md={md} className="mb-2">
    <small className="text-muted d-block">{label}</small>
    <span>{value || "—"}</span>
  </Col>
);

const CandidatoDetalhe = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { isAdmin } = useUserGroups();
  const { removeCandidate } = useCandidatos();
  const isMobile = useMediaQuery(MQ.belowMd);
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    // Always fetch full detail from API — the list cache only has summary data
    const fetchDetail = async () => {
      try {
        setupApiAuth(auth);
        const data = await ApiService.getCandidateDetail(id);
        const result = data.data || data;
        setCandidate(result);
      } catch (err) {
        setError(
          err.response?.status === 403
            ? "Acesso negado."
            : err.response?.status === 404
            ? "Candidato não encontrado."
            : "Erro ao carregar detalhes."
        );
      } finally {
        setLoading(false);
      }
    };
    if (auth.isAuthenticated) fetchDetail();
  }, [auth.isAuthenticated, id]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setDeleteError(null);
      setupApiAuth(auth);
      await ApiService.deleteCandidate(id);
      removeCandidate(id); // keep the cached list in sync — no ghost rows
      toast.success("Candidato excluído.");
      navigate("/candidatos");
    } catch (err) {
      console.error("Error deleting candidate:", err);
      setDeleteError(err.response?.data?.message || "Erro ao excluir candidato.");
    } finally {
      setDeleting(false);
    }
  };

  document.title = `${(candidate ? `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() : "") || "Candidato"} | ACIG Guapiaçu Cidadão`;

  if (loading) return (
    <div className="page-content">
      <Container fluid>
        <div className="text-center py-5"><Spinner color="primary" /></div>
      </Container>
    </div>
  );

  if (error) return (
    <div className="page-content">
      <Container fluid>
        <Alert color="danger">{error}</Alert>
        <Button color="secondary" onClick={() => navigate("/candidatos")}>Voltar</Button>
      </Container>
    </div>
  );

  const c = candidate;
  const cur = c?.curriculum;

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <Button color="secondary" outline size="sm" onClick={() => navigate(-1)} aria-label="Voltar para a lista">
              <i className="bx bx-arrow-back me-1" />Voltar
            </Button>
            <h4 className="mb-0 text-break">
              {[c.firstName, c.lastName].filter(Boolean).join(" ") || "Candidato"}
            </h4>
          </div>
          {isAdmin && (
            <Button color="danger" size="sm" onClick={() => setDeleteModalOpen(true)}>
              <i className="bx bx-trash me-1" />Excluir Candidato
            </Button>
          )}
        </div>

        {/* Personal info */}
        <Section title="Dados Pessoais">
          <Row>
            <Field label="E-mail" value={c.email} />
            <Field label="Nome" value={c.firstName} />
            <Field label="Sobrenome" value={c.lastName} />
            <Field label="Data de Nascimento" value={formatDateBR(c.dateOfBirth)} />
            <Field label="Idade" value={calcAge(c.dateOfBirth)} />
            <Field label="Gênero" value={GENDER_LABELS[c.gender]} />
            <Field label="Estado Civil" value={CIVIL_LABELS[c.estadoCivil]} />
            <Field label="CNH" value={c.hasCNH ? "Sim" : "Não"} />
          </Row>
        </Section>

        {/* Contact */}
        <Section title="Contato">
          <Row>
            <Field label="Telefone" value={c.phone} />
            <Field label="Celular" value={c.cellphone ? (
              <span className="d-inline-flex flex-wrap align-items-center gap-2">
                <a href={`tel:${c.cellphone.replace(/\D/g, "")}`}>{c.cellphone}</a>
                <a href={whatsappHref(c.cellphone)} target="_blank" rel="noopener noreferrer" className="btn btn-success btn-sm py-0 px-2">
                  <i className="bx bxl-whatsapp me-1" aria-hidden="true" />WhatsApp
                </a>
              </span>
            ) : null} />
            <Field label="Facebook" value={<SocialLink value={c.facebookProfile} base="https://facebook.com/" />} />
            <Field label="Instagram" value={<SocialLink value={c.instagramProfile} base="https://instagram.com/" />} />
            <Field label="LinkedIn" value={<SocialLink value={c.linkedinProfile} base="https://linkedin.com/in/" />} />
          </Row>
        </Section>

        {/* Address */}
        <Section title="Endereço">
          <Row>
            <Field label="Endereço" value={[c.address, c.addressNumber].filter(Boolean).join(", ")} />
            <Field label="Complemento" value={c.addressComplement} />
            <Field label="Bairro" value={c.neighborhood} />
            <Field label="Cidade" value={c.city} />
            <Field label="Estado" value={c.state} />
            <Field label="CEP" value={c.zipCode} />
          </Row>
        </Section>

        {/* Curriculum */}
        {cur && (
          <>
            <Section title="Currículo">
              <Row>
                <Field label="Sobre Mim" value={cur.aboutMe ? <span className="text-break" style={{ whiteSpace: "pre-wrap" }}>{cur.aboutMe}</span> : null} md={12} />
                <Field label="Escolaridade" value={EDUCATION_LABELS[cur.educationLevel]} />
                <Field label="Curso" value={cur.courseName === "Outro" ? cur.courseNameCustom : cur.courseName} />
                <Field label="Instituição" value={cur.collegeName} />
                <Field label="Situação" value={cur.studyStatus === "studying" ? "Cursando" : cur.studyStatus === "finished" ? "Concluído" : null} />
                <Field label="Período" value={cur.studyPeriod === "morning" ? "Manhã" : cur.studyPeriod === "afternoon" ? "Tarde" : cur.studyPeriod === "evening" ? "Noite" : null} />
              </Row>
            </Section>

            {/* Experiences */}
            {cur.experiences?.length > 0 && (
              <Section title="Experiências">
                {isMobile ? (
                  <div>
                    {cur.experiences.map((exp) => (
                      <div key={exp.id} className="list-card p-3">
                        <div className="fw-semibold">{exp.position}</div>
                        <div className="text-muted">{exp.company}</div>
                        <div className="text-muted small">
                          {formatDateBR(exp.startDate)} – {exp.isCurrentJob ? <Badge color="success">Atual</Badge> : (formatDateBR(exp.endDate) || "—")}
                        </div>
                        {exp.activities && <div className="small mt-1" style={{ whiteSpace: "pre-wrap" }}>{exp.activities}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Empresa</th>
                      <th>Cargo</th>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Atividades</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cur.experiences.map((exp) => (
                      <tr key={exp.id}>
                        <td>{exp.company}</td>
                        <td>{exp.position}</td>
                        <td>{formatDateBR(exp.startDate)}</td>
                        <td>{exp.isCurrentJob ? <Badge color="success">Atual</Badge> : formatDateBR(exp.endDate) || "—"}</td>
                        <td>{exp.activities || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                )}
              </Section>
            )}

            {/* Competencies */}
            {cur.competencies?.length > 0 && (
              <Section title="Competências">
                <Row>
                  {cur.competencies.map((comp) => (
                    <Col md={4} key={comp.id} className="mb-2">
                      <Card className="border mb-0">
                        <CardBody className="p-2">
                          <strong>{comp.customName || comp.name}</strong>
                          <Badge color="primary" className="ms-2">{LEVEL_LABELS[comp.level] || comp.level}</Badge>
                          {comp.description && <p className="text-muted small mb-0 mt-1">{comp.description}</p>}
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Section>
            )}
          </>
        )}

        {!cur && (
          <Alert color="warning">Este candidato ainda não preencheu o currículo.</Alert>
        )}

        {/* Delete confirmation modal */}
        <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
          <ModalHeader toggle={() => setDeleteModalOpen(false)}>Confirmar Exclusão</ModalHeader>
          <ModalBody>
            {deleteError && <Alert color="danger">{deleteError}</Alert>}
            <p>Tem certeza que deseja excluir o candidato <strong>{[c.firstName, c.lastName].filter(Boolean).join(" ")}</strong>?</p>
            <p className="text-danger mb-0">Esta ação não pode ser desfeita. A conta de acesso e todos os dados (perfil, currículo, experiências e competências) serão removidos. Um registro de auditoria será mantido.</p>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setDeleteModalOpen(false)} disabled={deleting}>Cancelar</Button>
            <Button color="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner size="sm" className="me-1" /> : null}
              Excluir
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default CandidatoDetalhe;
